import React from 'react';
import { UserModel } from './useUserManagement';
import { UserDetail } from './userTypes';
import { isOverrideActive, isOverrideExpired, isPaidActive, platformLabel } from './entitlementState';

const UserProStatus: React.FC<{ model: UserModel; user: UserDetail }> = ({ model, user: selectedUser }) => {
  const {
    premiumDuration, setPremiumDuration, premiumProcessing, handlePremiumToggle,
  } = model;

  const hasOverride = !!selectedUser.profile.premium_override;
  const exp = selectedUser.profile.premium_override_expires_at;
  const overrideActive = isOverrideActive(selectedUser.profile.premium_override, exp);
  const grantExpired = isOverrideExpired(selectedUser.profile.premium_override, exp);
  const expDate = exp ? new Date(exp) : null;
  const daysLeft = expDate && expDate > new Date() ? Math.ceil((expDate.getTime() - Date.now()) / 86400000) : null;

  const ent = selectedUser.entitlement;
  const paidFields = ent
    ? {
        entitlement_tier: ent.tier,
        entitlement_status: ent.status,
        entitlement_platform: ent.platform,
        entitlement_period_end: ent.period_end,
      }
    : null;
  const entitlementUnavailable = !!selectedUser.entitlement_unavailable;
  const paidActive = isPaidActive(paidFields);
  const statusStale = !!selectedUser.status_stale;

  // Effective state mirrors get_my_entitlement: an unexpired Admin grant wins,
  // then an active paid entitlement. When the entitlement read-back failed and
  // no grant exists, the paid side is genuinely unknown — show UNAVAILABLE
  // rather than guessing Free. When a refresh failed after the last verified
  // read, the same holds for every derived label: show it as STALE, never as
  // current.
  const lastKnownLabel = overrideActive
    ? paidActive
      ? 'PRO · GRANT + PAID'
      : 'PRO · ADMIN GRANT'
    : paidActive
      ? 'PRO · PAID'
      : grantExpired
        ? 'GRANT EXPIRED'
        : entitlementUnavailable
          ? 'STATUS UNAVAILABLE'
          : 'FREE';
  const effectiveLabel = statusStale ? `STALE · ${lastKnownLabel}` : lastKnownLabel;
  const effectiveColor =
    statusStale || grantExpired || entitlementUnavailable
      ? 'var(--admin-warning)'
      : overrideActive || paidActive
        ? 'var(--rp-primary)'
        : '#6b7280';

  const history = selectedUser.premium_history || [];
  const grants = history.filter(h => h.enabled === 'true');
  const revokes = history.filter(h => h.enabled === 'false');
  const totalMonthsGranted = grants.reduce((sum, h) => sum + (parseInt(h.duration_months || '0') || 0), 0);
  const lastGrant = grants.length > 0 ? grants[0] : null;

  return (
    <div className="rp-bento-card" style={{ borderLeft: `3px solid ${effectiveColor}` }}>
      {/* Header: effective Pro status (not proof — the grant/paid rows below are) */}
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-sm font-semibold" style={{ color: 'var(--rp-text-secondary)' }}>Pro Status</h3>
        <span
          className="px-2 py-0.5 rounded text-[10px] font-bold"
          style={{
            backgroundColor:
              statusStale || grantExpired || entitlementUnavailable
                ? 'rgba(245,158,11,0.15)'
                : overrideActive || paidActive
                  ? 'rgba(59,130,246,0.15)'
                  : 'rgba(107,114,128,0.15)',
            color: effectiveColor,
          }}
        >
          {effectiveLabel}
        </span>
      </div>
      {statusStale && (
        <p className="text-xs mb-3" style={{ color: 'var(--admin-warning)' }}>
          The last refresh failed — the status above is the last verified value, not the current one.
        </p>
      )}

      {/* Two-column layout: Paid entitlement | Admin Grant */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">

        {/* Paid entitlement (store_subscriptions → user_entitlements) */}
        <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--rp-surface-secondary)', border: `1px solid ${paidActive ? 'rgba(16,185,129,0.3)' : 'var(--rp-border)'}` }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold" style={{ color: 'var(--rp-text-tertiary)' }}>PAID ENTITLEMENT</span>
            {entitlementUnavailable ? (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: 'var(--admin-warning)' }}>UNKNOWN</span>
            ) : ent && ent.tier ? (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{
                backgroundColor: paidActive ? 'rgba(16,185,129,0.15)' : 'rgba(107,114,128,0.15)',
                color: paidActive ? 'var(--admin-success)' : '#6b7280'
              }}>
                {paidActive ? 'ACTIVE' : (ent.status || 'INACTIVE').toUpperCase()}
              </span>
            ) : (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: 'rgba(107,114,128,0.15)', color: '#6b7280' }}>NONE</span>
            )}
          </div>
          {entitlementUnavailable ? (
            <p className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>Store entitlement could not be verified. The Admin grant state at right is unaffected; refresh to retry the paid read-back.</p>
          ) : ent && ent.tier ? (
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span style={{ color: 'var(--rp-text-tertiary)' }}>Tier</span>
                <span className="capitalize" style={{ color: 'var(--rp-text)' }}>{ent.tier}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: 'var(--rp-text-tertiary)' }}>Platform</span>
                <span style={{ color: 'var(--rp-text)' }}>{platformLabel(ent.platform)}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: 'var(--rp-text-tertiary)' }}>Period end</span>
                <span style={{ color: 'var(--rp-text)' }}>{ent.period_end ? new Date(ent.period_end).toLocaleDateString() : 'None recorded'}</span>
              </div>
            </div>
          ) : (
            <p className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>No store entitlement on record.</p>
          )}
        </div>

        {/* Admin Grant Card */}
        <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--rp-surface-secondary)', border: `1px solid ${overrideActive ? 'rgba(59,130,246,0.3)' : 'var(--rp-border)'}` }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold" style={{ color: 'var(--rp-text-tertiary)' }}>ADMIN GRANT</span>
            {overrideActive ? (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: 'rgba(59,130,246,0.15)', color: 'var(--rp-primary)' }}>
                ACTIVE {exp ? `(${daysLeft}d)` : '(∞)'}
              </span>
            ) : grantExpired ? (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: 'var(--admin-warning)' }}>EXPIRED</span>
            ) : (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: 'rgba(107,114,128,0.15)', color: '#6b7280' }}>NONE</span>
            )}
          </div>

          {hasOverride ? (
            <div className="space-y-1.5 text-xs">
              {exp && (
                <div className="flex justify-between">
                  <span style={{ color: 'var(--rp-text-tertiary)' }}>{overrideActive ? 'Expires' : 'Expired'}</span>
                  <span style={{ color: 'var(--rp-text)' }}>{new Date(exp).toLocaleDateString()}</span>
                </div>
              )}
              {!exp && (
                <div className="flex justify-between">
                  <span style={{ color: 'var(--rp-text-tertiary)' }}>Duration</span>
                  <span style={{ color: 'var(--rp-primary)' }}>Permanent</span>
                </div>
              )}
              <div className="flex justify-between">
                <span style={{ color: 'var(--rp-text-tertiary)' }}>Total Granted</span>
                <span style={{ color: 'var(--rp-text)' }}>{totalMonthsGranted ? `${totalMonthsGranted}mo` : '∞'}</span>
              </div>
              {lastGrant && (
                <div className="flex justify-between">
                  <span style={{ color: 'var(--rp-text-tertiary)' }}>By</span>
                  <span style={{ color: 'var(--rp-text)' }}>{lastGrant.admin_email.replace('@ridepulse.app', '')}</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>No admin grant on record.</p>
          )}

          {/* Admin Grant Stats */}
          {history.length > 0 && (
            <div className="flex gap-3 mt-2 pt-2" style={{ borderTop: '1px solid var(--rp-border)' }}>
              <span className="text-[10px]" style={{ color: 'var(--admin-success)' }}>{grants.length} grant{grants.length !== 1 ? 's' : ''}</span>
              <span className="text-[10px]" style={{ color: 'var(--admin-error)' }}>{revokes.length} revoke{revokes.length !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      </div>

      {/* Controls: Grant / Revoke with safety */}
      <div className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: 'var(--rp-surface-secondary)' }}>
        <div className="flex items-center gap-2">
          {/* Duration selector for grants */}
          <div className="flex items-center gap-0.5 rounded-lg p-0.5" role="group" aria-label="Grant duration" style={{ backgroundColor: 'var(--rp-surface)' }}>
            {([{ label: '1mo', months: 1 }, { label: '3mo', months: 3 }, { label: '6mo', months: 6 }, { label: '12mo', months: 12 }, { label: '∞', months: null }] as const).map(opt => (
              <button
                key={opt.label}
                onClick={() => setPremiumDuration(opt.months)}
                className="px-1.5 py-0.5 rounded text-[10px] font-bold transition-all"
                style={{
                  minWidth: 28, minHeight: 28,
                  backgroundColor: premiumDuration === opt.months ? 'rgba(59,130,246,0.3)' : 'transparent',
                  color: premiumDuration === opt.months ? 'var(--rp-primary)' : 'var(--rp-text-tertiary)',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => handlePremiumToggle(selectedUser.id, selectedUser.email, true)}
            disabled={premiumProcessing}
            className="admin-btn-secondary px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50"
            style={{ color: 'var(--rp-primary)', borderColor: 'rgba(59,130,246,0.3)' }}
          >
            {premiumProcessing ? '...' : 'Grant'}
          </button>
        </div>
        <div className="flex items-center gap-2">
          {overrideActive && (
            <button
              onClick={() => handlePremiumToggle(selectedUser.id, selectedUser.email, false)}
              disabled={premiumProcessing}
              className="admin-btn-secondary px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50"
              style={{ color: 'var(--admin-error)', borderColor: 'rgba(239,68,68,0.3)' }}
            >
              {premiumProcessing ? '...' : 'Revoke Grant'}
            </button>
          )}
          {paidActive && !overrideActive && (
            <span className="text-[10px] px-2 py-1 rounded" style={{ backgroundColor: 'rgba(16,185,129,0.1)', color: 'var(--admin-success)' }}>
              Paid subscriber — managed via app store
            </span>
          )}
          {paidActive && overrideActive && (
            <span className="text-[10px] italic" style={{ color: 'var(--rp-text-tertiary)' }}>
              Revoking the grant leaves the paid entitlement active
            </span>
          )}
        </div>
      </div>

      {/* History log */}
      {history.length > 0 && (
        <details className="mt-3">
          <summary className="text-[11px] font-semibold cursor-pointer" style={{ color: 'var(--rp-text-tertiary)' }}>
            Admin Grant History ({history.length} actions)
          </summary>
          <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
            {history.map((h, i) => (
              <div key={i} className="flex items-center justify-between text-[10px] p-1.5 rounded" style={{ backgroundColor: 'var(--rp-surface-secondary)' }}>
                <div className="flex items-center gap-2">
                  <span style={{ color: h.enabled === 'true' ? 'var(--admin-success)' : 'var(--admin-error)' }}>
                    {h.enabled === 'true' ? '▲ Granted' : '▼ Revoked'}
                  </span>
                  {h.enabled === 'true' && h.duration_months && (
                    <span style={{ color: 'var(--rp-text-secondary)' }}>{h.duration_months}mo</span>
                  )}
                  {h.enabled === 'true' && !h.duration_months && (
                    <span style={{ color: 'var(--rp-text-secondary)' }}>∞</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span style={{ color: 'var(--rp-text-tertiary)' }}>{h.admin_email.replace('@ridepulse.app', '')}</span>
                  <span style={{ color: 'var(--rp-text-tertiary)' }}>{new Date(h.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default UserProStatus;
